import * as giphyFetch from "@circle-react/helpers/giphyFetchHelpers";
import { SearchInput as CompassSearchInput } from "@circleco/compass/components/SearchInput";
import { flatten, uniqBy } from "lodash";
import { useEffect, useMemo, useRef, useState } from "react";
import { useInfiniteQuery } from "react-query";

import { t } from "@/i18n-js/instance";
import { useIsCircleV4FeatureEnabled } from "@/react/hooks/useIsCircleV4FeatureEnabled";

import { PickerBody } from "./PickerBody";
import { SearchBox } from "./SearchBox";

const PAGE_SIZE = 10;
const SEARCH_DEBOUNCE_MS = 300;

interface GiphyPickerProps {
  close: () => void;
}

export const GiphyPicker = ({ close }: GiphyPickerProps) => {
  const [query, setQuery] = useState<string | null>(null);
  const isCompassEnabled = useIsCircleV4FeatureEnabled(
    "circle_v4_popover_enabled",
  );
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isCompassEnabled) searchInputRef.current?.focus();
  }, [isCompassEnabled]);

  const fetchGifs = ({ pageParam = 1 }) =>
    query
      ? giphyFetch.search(query, { offset: pageParam, limit: PAGE_SIZE })
      : giphyFetch.trending({ offset: pageParam, limit: PAGE_SIZE });

  const { data, fetchNextPage, hasNextPage, isInitialLoading } =
    useInfiniteQuery({
      initialPageParam: 1,
      queryKey: ["chat-members-search", query],
      queryFn: fetchGifs,
      getNextPageParam: lastPage => {
        const loadedItems =
          lastPage.pagination.offset + lastPage.pagination.count;
        const totalItems = lastPage.pagination.total_count;
        return loadedItems < totalItems
          ? lastPage.pagination.offset + PAGE_SIZE
          : null;
      },
    });

  const gifs = useMemo(
    () => uniqBy(flatten(data?.pages?.map(page => page.data)), "id"),
    [data],
  );

  const wrapperClassName = isCompassEnabled
    ? "w-[408px]"
    : "bg-primary border-primary flex w-[360px] flex-col space-y-1 rounded-sm border shadow";

  return (
    <div className={wrapperClassName} data-testid="giphy-picker">
      {isCompassEnabled ? (
        <div className="mb-4">
          <CompassSearchInput
            ref={searchInputRef}
            placeholder={t("tiptap.search_giphy")}
            onSearch={value => setQuery(value || null)}
            debounceMs={SEARCH_DEBOUNCE_MS}
            onClear={() => setQuery(null)}
          />
        </div>
      ) : (
        <SearchBox query={query} setQuery={setQuery} />
      )}
      <div className="h-[360px] overflow-hidden">
        <PickerBody
          gifs={gifs}
          isInitialLoading={isInitialLoading}
          isCompassEnabled={isCompassEnabled}
          hasNextPage={Boolean(hasNextPage)}
          fetchNextPage={fetchNextPage}
          close={close}
        />
      </div>
    </div>
  );
};
